import _ from "lodash";
import React, { memo, useMemo } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { colors } from "Constants";
import { useI18n } from "Hooks";
import { goToRelativeRoute } from "modules/Services";
import { compose } from "lib/apolloClient";
import { formatDate } from "Utils/DateTimeHelpers";
import { getCommentDisplayContent } from "Utils/RegexHelper";
import { highlightTextOutsideAnchorTag } from "Utils/highlightText";
import {
  ATTACHMENT_TYPES,
  DATE_FORMATS,
  MESSAGE_STATUS,
  areMomentsSame,
  getMentionsHtml,
  renderLinkInMessage,
  isMessagingRevempEnabled,
  MESSAGE_TYPE,
  isMessagingReadReceiptEnabled,
} from "MessagingV2/modules/Utils";
import { getMessagingV2ReduxState } from "MessagingV2/modules/MessagingModule";
import MultiTypeAttachments from "MessagingV2/components/Messages/MultiTypeAttachments";
import {
  DoubleVisualMediaContainer,
  GridVisualMediaContainer,
  SingleVisualMediaContainer,
  TripleVisualMediaContainer,
} from "MessagingV2/components/Messages/VisualMedia";
import ParentMessage from "MessagingV2/components/ParentMessage";
import ReadReceipt from "../ReadReceipt";

import classes from "./MessageBubble.scss";

const IMAGE_OR_VIDEO_TYPES = [ATTACHMENT_TYPES.IMAGE, ATTACHMENT_TYPES.VIDEO];

const Attachments = ({ totalAttachments, attachments, onMediaClick }) => {
  const isOnlyImagesOrVideos = _.every(attachments, singleAttachment =>
    _.includes(IMAGE_OR_VIDEO_TYPES, singleAttachment?.type)
  );

  if (isOnlyImagesOrVideos) {
    switch (totalAttachments) {
      case 1:
        return (
          <SingleVisualMediaContainer
            attachment={_.first(attachments)}
            onMediaClick={onMediaClick}
          />
        );
      case 2:
        return (
          <DoubleVisualMediaContainer
            attachments={attachments}
            onMediaClick={onMediaClick}
          />
        );
      case 3:
        return (
          <TripleVisualMediaContainer
            attachments={attachments}
            onMediaClick={onMediaClick}
          />
        );
      default:
        return (
          <GridVisualMediaContainer
            attachments={attachments}
            onMediaClick={onMediaClick}
          />
        );
    }
  }

  return (
    <MultiTypeAttachments
      attachments={attachments}
      onMediaClick={onMediaClick}
    />
  );
};

Attachments.propTypes = {
  totalAttachments: PropTypes.number.isRequired,
  attachments: PropTypes.array.isRequired,
  onMediaClick: PropTypes.func.isRequired,
};

const MessageLabelWithoutConnect = ({
  label,
  searchText,
  isEditedMessage = false,
}) => {
  const { t } = useI18n(["integration"]);
  const labelClasses = classNames(
    "text-body-2",
    "text-textDefault",
    classes.message
  );
  const editedMessageClass = classNames("text-body-3", classes.editedMessage);
  const isUIRevempEnabled = isMessagingRevempEnabled();

  const parsedLabel = useMemo(() => {
    const linkedLabel = getCommentDisplayContent(label, {
      getHtml: getMentionsHtml,
      autoLinkReplaceFn: match => {
        switch (match.getType()) {
          case "url":
            return renderLinkInMessage({ match, searchText });
          default:
            // return true to let the Autolinker(library) continue
            return true;
        }
      },
    });

    return searchText
      ? highlightTextOutsideAnchorTag({
          item: linkedLabel,
          keyword: searchText,
        })
      : linkedLabel;
  }, [label, searchText]);

  return (
    <>
      <span
        className={labelClasses}
        dangerouslySetInnerHTML={{ __html: parsedLabel }}
      />
      {isEditedMessage && isUIRevempEnabled ? (
        <span className={editedMessageClass}>{`(${t(
          "integration:edited"
        )})`}</span>
      ) : null}
    </>
  );
};

const mapStateToPropsForLabel = state => ({
  searchText: _.get(getMessagingV2ReduxState(state), "searchText", ""),
});

const MessageLabel = connect(mapStateToPropsForLabel)(
  MessageLabelWithoutConnect
);

const MessageBubble = props => {
  const {
    openPreviewModal,
    message,
    isSender,
    status,
    onParentMessageClick,
    isMessageHighlighted,
  } = props;

  const {
    label,
    isRead,
    parentMessage,
    attachments,
    createdAt,
    updatedAt,
  } = message;

  const { t } = useI18n(["integration"]);
  const parentMessageType = _.get(parentMessage, "type", "NORMAL");
  const isForwardedMessage = _.isEqual(
    parentMessageType,
    MESSAGE_TYPE.FORWARDED_MESSAGE
  );

  const isSent = !status;
  const isEditedMessage =
    isSent &&
    !!updatedAt &&
    !areMomentsSame({ date1: createdAt, date2: updatedAt });

  const totalAttachments = _.size(attachments);

  const isUIRevempEnabled = isMessagingRevempEnabled();

  const messageBubbleClasses = classNames(classes.messageBubbleContainer, {
    [classes.senderMessageBubble]: isSender,
    [classes.receiverMessageBubble]: !isSender,
    [classes.bubbleWithAttachments]: totalAttachments > 0,
    [classes.highlightedMessageBubble]: isMessageHighlighted,
  });

  const messageBubbleClassesV2 = classNames(classes.messageBubbleContainerV2, {
    [classes.bubbleWithAttachments]: totalAttachments > 0,
  });

  const footerTextClasses = classNames(
    "text-body-3",
    "text-textSubtle",
    classes.footerText
  );

  const inlineSpaceClasses = classNames(classes.footerInlineSpace, {
    [classes.sentFooterInlineSpace]: isSender && !isEditedMessage,
    [classes.sentEditedFooterInlineSpace]: isSender && isEditedMessage,
    [classes.receivedFooterInlineSpace]: !isSender && !isEditedMessage,
    [classes.receivedEditedFooterInlineSpace]: !isSender && isEditedMessage,
  });

  const messageStatusFooterClasses = classNames(classes.messageStatusFooter, {
    [classes.absoluteFooter]: !!label,
  });
  const editedMessageClass = classNames("text-body-3", classes.editedMessage);

  const onMediaClick = selectedAttachment => {
    // if message is in sending or failed state, don't open preview modal
    if (!isSent) return;
    openPreviewModal({ attachments, selectedAttachment });
  };

  const style = {
    "--background-color": isSender ? colors.surfaceLink : colors.surfaceDefault,
    "--highlight-color": isSender ? colors.blue900 : colors.surfaceDefaultHover,
  };

  const canShowReadReceipt = isMessagingReadReceiptEnabled();
  return (
    <div
      style={style}
      className={
        isUIRevempEnabled ? messageBubbleClassesV2 : messageBubbleClasses
      }
    >
      {label && isForwardedMessage ? (
        <div className={classes.messageLabel}>
          <MessageLabel label={label} isEditedMessage={isEditedMessage} />
        </div>
      ) : null}

      {totalAttachments && isUIRevempEnabled && isForwardedMessage ? (
        <div className={classes.messageAttachmentContainer}>
          <Attachments
            totalAttachments={totalAttachments}
            attachments={attachments}
            onMediaClick={onMediaClick}
          />
        </div>
      ) : null}
      {parentMessage ? (
        <ParentMessage
          isChildMessageSentByUser={isSender}
          parentMessage={parentMessage}
          goToParentMessage={onParentMessageClick}
        />
      ) : null}

      {totalAttachments && !isUIRevempEnabled ? (
        <Attachments
          totalAttachments={totalAttachments}
          attachments={attachments}
          onMediaClick={onMediaClick}
        />
      ) : null}

      {label ? (
        <div className={classes.messageLabel}>
          {isForwardedMessage ? null : (
            <MessageLabel label={label} isEditedMessage={isEditedMessage} />
          )}
          {status !== MESSAGE_STATUS.FAILED && !isUIRevempEnabled ? (
            <span className={inlineSpaceClasses} />
          ) : null}
        </div>
      ) : null}

      {totalAttachments && isUIRevempEnabled && !isForwardedMessage ? (
        <div className={classes.messageAttachmentContainer}>
          <Attachments
            totalAttachments={totalAttachments}
            attachments={attachments}
            onMediaClick={onMediaClick}
          />
        </div>
      ) : null}

      {isUIRevempEnabled && !label && totalAttachments && isEditedMessage ? (
        <div className={editedMessageClass}>{`(${t(
          "integration:edited"
        )})`}</div>
      ) : null}

      {status !== MESSAGE_STATUS.FAILED ? (
        <div className={messageStatusFooterClasses}>
          {isEditedMessage && !isUIRevempEnabled ? (
            <>
              <div className={footerTextClasses}>{t("integration:edited")}</div>
              <div className={footerTextClasses}>|</div>
            </>
          ) : null}
          {isUIRevempEnabled ? null : (
            <div className={classes.dateAndIconWrapper}>
              <div className={footerTextClasses}>
                {formatDate({
                  date: createdAt,
                  format: DATE_FORMATS.SENT_MESSAGE,
                })}
              </div>
              {canShowReadReceipt ? (
                <ReadReceipt
                  isSent={isSent}
                  isRead={isRead}
                  isSender={isSender}
                />
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

MessageBubble.propTypes = {
  openPreviewModal: PropTypes.func.isRequired,
  message: PropTypes.object.isRequired,
  isSender: PropTypes.bool.isRequired,
  status: PropTypes.oneOf(_.values(MESSAGE_STATUS)),
  onParentMessageClick: PropTypes.func.isRequired,
  isMessageHighlighted: PropTypes.bool.isRequired,
};

const mapDispatchToProps = { goToRelativeRoute };

export default compose(
  connect(undefined, mapDispatchToProps),
  memo
)(MessageBubble);
